import { db, storage } from '../firebase';
import { makeKey } from './categoryHelpers';
import { encryptForUser, decryptForUser } from './localCrypto';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import {
  ref,
  uploadString,
  getDownloadURL,
} from 'firebase/storage';

function userFilesRef(uid) {
  return collection(db, 'users', uid, 'expenseFiles');
}

function userTransactionFilesRef(uid) {
  return collection(db, 'users', uid, 'transactionFiles');
}

function userTransactionAccountsRef(uid) {
  return collection(db, 'users', uid, 'transactionAccounts');
}

export async function saveTransactionAccount(uid, { name, reportType, type, openingBalance, openingBalanceDate }) {
  const docRef = doc(userTransactionAccountsRef(uid), name);
  const existing = await getDoc(docRef);
  const payload = { name, reportType, type };
  if (openingBalance !== undefined) payload.openingBalance = openingBalance;
  if (openingBalanceDate !== undefined) payload.openingBalanceDate = openingBalanceDate;
  if (existing.exists()) {
    await updateDoc(docRef, payload);
  } else {
    await setDoc(docRef, { ...payload, createdAt: serverTimestamp() });
  }
  return { id: name, ...payload };
}

export async function updateTransactionAccount(uid, name, patch) {
  const docRef = doc(userTransactionAccountsRef(uid), name);
  await setDoc(docRef, patch, { merge: true });
}

// Creates the account record only if one doesn't exist yet (lazy backfill for legacy accounts).
export async function ensureTransactionAccount(uid, { name, reportType }) {
  const ref = doc(userTransactionAccountsRef(uid), name);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  const record = { name, reportType, type: 'personal', createdAt: serverTimestamp() };
  await setDoc(ref, record);
  return { id: name, ...record };
}

export async function listTransactionAccounts(uid) {
  const snap = await getDocs(userTransactionAccountsRef(uid));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() ?? 0;
      const bTime = b.createdAt?.toMillis?.() ?? 0;
      return aTime !== bTime ? aTime - bTime : (a.name ?? '').localeCompare(b.name ?? '');
    });
}

function isTransactionReportType(reportType) {
  return reportType === 'credit_card' || reportType === 'bank' || reportType === 'brokerage';
}

function metaRef(uid) {
  return doc(db, 'users', uid, 'meta', 'prefs');
}

function imageStoragePath(uid, expenseId) {
  return `users/${uid}/images/${expenseId}`;
}

// Returns true if the string is a base64 data URL (not already a remote URL)
function isDataUrl(str) {
  return typeof str === 'string' && str.startsWith('data:');
}

// Upload a base64 data URL to Storage, return the download URL
async function uploadImage(uid, expenseId, dataUrl) {
  // console.log("ready to store dataurl:", dataUrl, dataUrl.length);
  const storageRef = ref(storage, imageStoragePath(uid, expenseId));
  // dataUrl is "data:<mimeType>;base64,<data>" — uploadString handles this format directly
  await uploadString(storageRef, dataUrl, 'data_url');
  return getDownloadURL(storageRef);
}

// Strip base64 imagePreview from each expense, upload to Storage, replace with URL
async function uploadExpenseImages(uid, expenses) {
  return Promise.all(
    expenses.map(async (expense) => {
      if (!expense.imagePreview || !isDataUrl(expense.imagePreview)) {
        // Already a URL or no image — nothing to upload
        return expense;
      }
      try {
        const url = await uploadImage(uid, expense.id, expense.imagePreview);
        return { ...expense, imagePreview: url };
      } catch (err) {
        console.error(`Failed to upload image for expense ${expense.id}:`, err);
        // Drop the image rather than fail the whole save
        return { ...expense, imagePreview: null };
      }
    })
  );
}


export async function saveExpenseFile(uid, filename, expenses, { imageStorage = true, reportType } = {}) {
  // Upload base64 images to Storage — or strip them if the user's tier doesn't include storage
  const expensesWithUrls = imageStorage
    ? await uploadExpenseImages(uid, expenses)
    : expenses.map(e => ({ ...e, imagePreview: isDataUrl(e.imagePreview) ? null : e.imagePreview }));

  const fileDoc = doc(userFilesRef(uid), filename);
  const payload = {
    name: filename,
    data: JSON.stringify(expensesWithUrls),
    updatedAt: serverTimestamp(),
  };
  if (reportType) payload.reportType = reportType;

  await setDoc(fileDoc, payload, { merge: true }); // merge preserves isShared / sharedWith set by sharingStorage

  // Return the updated expenses (with URLs) so the caller can update local state
  return expensesWithUrls;
}

export async function updateLastOpenedFile(uid, filename) {
  await setDoc(metaRef(uid), { lastFile: filename }, { merge: true });
}

/**
 * Load multiple expense files by name in parallel.
 * Files that fail to load are omitted from the result rather than throwing.
 */
export async function loadMultipleExpenseFiles(uid, filenames) {
  const results = await Promise.allSettled(
    filenames.map(name => loadExpenseFile(uid, name))
  );
  return results
    .map(r => (r.status === 'fulfilled' && r.value) ? r.value : null)
    .filter(Boolean);
}

export async function loadExpenseFile(uid, filename) {
  const fileDoc = doc(userFilesRef(uid), filename);
  const snap = await getDoc(fileDoc);
  if (!snap.exists()) return null;
  const d = snap.data();
  return { name: d.name, data: JSON.parse(d.data), reportType: d.reportType ?? 'expense' };
}

export async function deleteExpenseFile(uid, filename) {
  await deleteDoc(doc(userFilesRef(uid), filename));
}

export async function listExpenseFiles(uid) {
  const q = query(userFilesRef(uid), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      name: data.name,
      data: JSON.parse(data.data),
      isShared: data.isShared ?? false,
      reportType: data.reportType ?? 'expense',
    };
  });
}

export async function getLastOpenedFile(uid) {
  const snap = await getDoc(metaRef(uid));
  if (!snap.exists()) return null;
  const prefs = snap.data();
  if (!prefs.lastFile) return null;
  return loadExpenseFile(uid, prefs.lastFile);
}

export async function loadMostRecentFile(uid) {
  const q = query(userFilesRef(uid), orderBy('updatedAt', 'desc'), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const data = snap.docs[0].data();
  return { name: data.name, data: JSON.parse(data.data) };
}

// ─── Custom categories ────────────────────────────────────────────────────────

const LS_CATEGORIES_KEY = 'expenser_custom_categories';

function categoriesRef(uid) {
  return doc(db, 'users', uid, 'meta', 'categories');
}

function normalizeCustomCat(c) {
  if (c.key && c.label) return c;
  const label = c.label ?? c.name ?? '';
  return { key: c.key ?? makeKey(label), label, taxCategory: c.taxCategory };
}

export async function loadCustomCategories(uid) {
  if (uid) {
    try {
      const snap = await getDoc(categoriesRef(uid));
      if (!snap.exists()) return [];
      return JSON.parse(snap.data().data || '[]').map(normalizeCustomCat);
    } catch {
      return [];
    }
  }
  try {
    return JSON.parse(localStorage.getItem(LS_CATEGORIES_KEY) || '[]').map(normalizeCustomCat);
  } catch {
    return [];
  }
}

export async function saveCustomCategories(uid, categories) {
  if (uid) {
    await setDoc(categoriesRef(uid), { data: JSON.stringify(categories) }, { merge: false });
  } else {
    localStorage.setItem(LS_CATEGORIES_KEY, JSON.stringify(categories));
  }
}

function incomeCategoriesRef(uid) {
  return doc(db, 'users', uid, 'meta', 'incomeCategories');
}

// ─── Preset views ─────────────────────────────────────────────────────────────

function presetViewsRef(uid) {
  return doc(db, 'users', uid, 'meta', 'presetViews');
}

export async function loadPresetViews(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(presetViewsRef(uid));
    if (!snap.exists()) return null;
    return JSON.parse(snap.data().data || 'null');
  } catch {
    return null;
  }
}

export async function savePresetViews(uid, views) {
  if (!uid) return;
  await setDoc(presetViewsRef(uid), { data: JSON.stringify(views) }, { merge: false });
}

export async function loadCustomIncomeCategories(uid) {
  if (!uid) return [];
  try {
    const snap = await getDoc(incomeCategoriesRef(uid));
    if (!snap.exists()) return [];
    return JSON.parse(snap.data().data || '[]').map(normalizeCustomCat);
  } catch {
    return [];
  }
}

export async function saveCustomIncomeCategories(uid, categories) {
  if (!uid) return;
  await setDoc(incomeCategoriesRef(uid), { data: JSON.stringify(categories) }, { merge: false });
}

// ─── User preferences (currency + adjustment factor) ─────────────────────────

const LS_PREFS_KEY = 'rivnus_prefs';
const DEFAULT_PREFS = { defaultCurrency: 'USD', adjustmentFactor: 0 };

export async function getPreferences(uid) {
  if (uid) {
    try {
      const snap = await getDoc(metaRef(uid));
      if (!snap.exists()) return DEFAULT_PREFS;
      const d = snap.data();
      return {
        defaultCurrency:  d.defaultCurrency  ?? 'USD',
        adjustmentFactor: d.adjustmentFactor ?? 0,
      };
    } catch {
      return DEFAULT_PREFS;
    }
  }
  try {
    const raw = localStorage.getItem(LS_PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

export async function savePreferences(uid, prefs) {
  if (uid) {
    await setDoc(metaRef(uid), prefs, { merge: true });
  } else {
    localStorage.setItem(LS_PREFS_KEY, JSON.stringify(prefs));
  }
}

// ─── Transaction files (CC / bank CSV imports) ────────────────────────────────

const localTxKey = (uid, filename) => `local_tx_${uid}_${filename}`;

async function saveLocalTx(uid, filename, transactions) {
  const encrypted = await encryptForUser(uid, JSON.stringify(transactions));
  localStorage.setItem(localTxKey(uid, filename), encrypted);
}

async function loadLocalTx(uid, filename) {
  try {
    const raw = localStorage.getItem(localTxKey(uid, filename));
    if (!raw) return null;
    const json = await decryptForUser(uid, raw);
    return json ? JSON.parse(json) : null;
  } catch { return null; }
}

function deleteLocalTx(uid, filename) {
  localStorage.removeItem(localTxKey(uid, filename));
}

export async function saveTransactionFile(uid, filename, transactions, { reportType, accountName, type, local = false } = {}) {
  if (local) {
    await saveLocalTx(uid, filename, transactions);
    const fileDoc = doc(userTransactionFilesRef(uid), filename);
    const localPayload = {
      name: filename,
      reportType: reportType ?? 'credit_card',
      accountName: accountName ?? null,
      isLocal: true,
      updatedAt: serverTimestamp(),
    };
    if (type != null) localPayload.type = type;
    await setDoc(fileDoc, localPayload, { merge: true });
    return transactions;
  }
  const fileDoc = doc(userTransactionFilesRef(uid), filename);
  const payload = {
    name: filename,
    data: JSON.stringify(transactions),
    updatedAt: serverTimestamp(),
  };
  if (reportType) payload.reportType = reportType;
  if (accountName) payload.accountName = accountName;
  if (type != null) payload.type = type;
  await setDoc(fileDoc, payload, { merge: true });
  return transactions;
}

export async function loadTransactionFile(uid, filename) {
  const fileDoc = doc(userTransactionFilesRef(uid), filename);
  const snap = await getDoc(fileDoc);
  if (!snap.exists()) return null;
  const d = snap.data();
  if (d.isLocal) {
    const data = await loadLocalTx(uid, filename);
    return {
      name: d.name,
      data: data ?? [],
      reportType: d.reportType ?? 'credit_card',
      accountName: d.accountName ?? null,
      type: d.type ?? null,
      local: true,
      localDataMissing: data === null,
    };
  }
  return {
    name: d.name,
    data: JSON.parse(d.data),
    reportType: d.reportType ?? 'credit_card',
    accountName: d.accountName ?? null,
    type: d.type ?? null,
  };
}

export async function restoreLocalTx(uid, filename, data) {
  await saveLocalTx(uid, filename, data);
}

export async function deleteTransactionFile(uid, filename) {
  deleteLocalTx(uid, filename);
  await deleteDoc(doc(userTransactionFilesRef(uid), filename));
}

export async function listTransactionFiles(uid) {
  const q = query(userTransactionFilesRef(uid), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return Promise.all(snap.docs.map(async (d) => {
    const data = d.data();
    const updatedAt = data.updatedAt?.toDate?.() ?? null;
    if (data.isLocal) {
      const localData = await loadLocalTx(uid, data.name);
      return {
        name: data.name,
        data: localData ?? [],
        isShared: false,
        reportType: data.reportType ?? 'credit_card',
        accountName: data.accountName ?? null,
        type: data.type ?? null,
        local: true,
        localDataMissing: localData === null,
        updatedAt,
      };
    }
    return {
      name: data.name,
      data: JSON.parse(data.data),
      isShared: false,
      reportType: data.reportType ?? 'credit_card',
      accountName: data.accountName ?? null,
      type: data.type ?? null,
      updatedAt,
    };
  }));
}

// ─── Generic dispatchers (route by reportType) ────────────────────────────────

export async function saveFile(uid, filename, data, opts = {}) {
  if (isTransactionReportType(opts.reportType)) {
    return saveTransactionFile(uid, filename, data, { reportType: opts.reportType, accountName: opts.accountName, type: opts.type ?? null, local: opts.local ?? false });
  }
  return saveExpenseFile(uid, filename, data, opts);
}

export async function loadFile(uid, filename, reportType) {
  if (isTransactionReportType(reportType)) {
    return loadTransactionFile(uid, filename);
  }
  return loadExpenseFile(uid, filename);
}

export async function deleteFile(uid, filename, reportType) {
  if (isTransactionReportType(reportType)) {
    return deleteTransactionFile(uid, filename);
  }
  return deleteExpenseFile(uid, filename);
}

export async function listAllFiles(uid) {
  const [expFiles, txFiles] = await Promise.all([
    listExpenseFiles(uid),
    listTransactionFiles(uid),
  ]);
  return [...expFiles, ...txFiles];
}

// ─── Reimbursements ───────────────────────────────────────────────────────────

// Mark individual expense/transaction items as reimbursed in their source files.
// items: Array<{ filename, id, source_type: 'transaction'|'expense_report' }>
// Returns: Array<{ filename, source_type, data }> — updated data arrays for in-memory patching.
export async function markItemsReimbursed(uid, items, value = true) {
  // Group by (source_type, filename)
  const groups = {};
  for (const item of items) {
    const key = `${item.source_type}::${item.filename}`;
    if (!groups[key]) groups[key] = { source_type: item.source_type, filename: item.filename, ids: new Set() };
    groups[key].ids.add(String(item.id));
  }

  const results = [];
  await Promise.all(Object.values(groups).map(async ({ source_type, filename, ids }) => {
    const coll  = source_type === 'transaction' ? userTransactionFilesRef(uid) : userFilesRef(uid);
    const docRef = doc(coll, filename);
    const snap   = await getDoc(docRef);
    if (!snap.exists()) return;
    const raw  = snap.data();
    const data = JSON.parse(raw.data || '[]').map(item =>
      ids.has(String(item.id)) ? { ...item, reimbursed: value || undefined } : item
    );
    await updateDoc(docRef, { data: JSON.stringify(data), updatedAt: serverTimestamp() });
    results.push({ filename, source_type, data });
  }));
  return results;
}

